import {
  useMyPaymentTermsWithPending,
  useGenerateInvoice,
  type PaymentTermWithPending,
  type PendingPaymentTermGroup,
} from "@/api/use-invoices";
import axios from "axios";

type TermWithRequiredPending = PaymentTermWithPending & {
  pending: PendingPaymentTermGroup;
};

import { Button, Card, CardBody, Chip, Progress } from "@heroui/react";
import { FileText, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

function formatCurrency(cents: number) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

export function PendingInvoiceCard() {
  const { t } = useTranslation("app");
  const { data, isLoading } = useMyPaymentTermsWithPending();
  const generateInvoice = useGenerateInvoice();
  const { toast } = useToast();

  if (isLoading || !data) return null;

  const termsWithPending = (data.paymentTerms ?? []).filter(
    (term): term is TermWithRequiredPending =>
      !!term.pending && term.pending.completedOrders > 0
  );

  if (termsWithPending.length === 0) return null;

  const creditUsage = data.creditUsage;

  const handleGenerate = async (paymentTermsId: string) => {
    try {
      await generateInvoice.mutateAsync(paymentTermsId);
      toast({
        title: t("invoices.pending.generatedTitle"),
        description: t("invoices.pending.generatedDescription"),
      });
    } catch (error: unknown) {
      const message = axios.isAxiosError<{ error?: string }>(error)
        ? error.response?.data?.error
        : undefined;
      toast({
        title: t("invoices.pending.errorTitle"),
        description: message ?? t("invoices.pending.generateError"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-3">
      {termsWithPending.map((term) => (
        <Card
          key={term.id}
          shadow="none"
          className="border border-primary/20 bg-primary/5"
        >
          <CardBody className="p-4 md:p-5">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-primary">
                    {term.name}
                  </span>
                  <Chip variant="flat" size="sm" color="default">
                    {t("invoices.pending.everyDays", {
                      count: term.dayInterval,
                    })}
                  </Chip>
                </div>
                <Chip color="primary" variant="flat" size="sm">
                  {t("invoices.pending.dueInDays", {
                    count: term.paymentDueDays,
                  })}
                </Chip>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-default-500 text-xs">
                    {t("invoices.pending.pendingOrders")}
                  </p>
                  <p className="font-medium">
                    {t("invoices.pending.completedCount", {
                      count: term.pending.completedOrders,
                    })}
                    {term.pending.totalOrders >
                      term.pending.completedOrders && (
                      <span className="text-default-500">
                        {" "}
                        {t("invoices.pending.totalCount", {
                          count: term.pending.totalOrders,
                        })}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-default-500 text-xs">
                    {t("invoices.pending.amount")}
                  </p>
                  <p className="font-medium">
                    {formatCurrency(term.pending.completedTotalCents)}
                  </p>
                </div>
                <div>
                  <p className="text-default-500 text-xs">
                    {t("invoices.pending.nextAutoInvoice")}
                  </p>
                  <p className="font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {t("invoices.pending.days", {
                      count: term.daysUntilAutoInvoice,
                    })}
                  </p>
                </div>
              </div>

              {creditUsage && creditUsage.creditLimit > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-default-500">
                      {t("invoices.pending.credit", {
                        open: formatCurrency(creditUsage.outstandingBalance),
                        limit: formatCurrency(creditUsage.creditLimit),
                      })}
                    </span>
                    <span className="text-default-500">
                      {creditUsage.utilizationPercent}%
                    </span>
                  </div>
                  <Progress
                    aria-label={t("invoices.pending.creditUsage")}
                    value={creditUsage.utilizationPercent}
                    size="sm"
                  />
                </div>
              )}

              <Button
                color="primary"
                onPress={() => handleGenerate(term.id)}
                isDisabled={generateInvoice.isPending}
                isLoading={generateInvoice.isPending}
                className="w-full sm:w-auto sm:self-end"
              >
                {t("invoices.pending.generateNow")}
              </Button>
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
